// Right-angle link cables between two objects, with animated shader flow.
// Trimmed port of the link-rebuild logic in oq-images/SceneBuilder.tsx.
// Drops the A* router (objects-as-obstacles) — we use the simple L-shape
// router in phase 2; A* can be added later if it matters.
import * as THREE from "three";
import type { LinkConnection, SceneObject } from "./types";

/** Side of an axis-aligned footprint box, oriented by `other`. */
type Side = "+x" | "-x" | "+z" | "-z";

function pickSide(box: THREE.Box3, other: THREE.Vector3): Side {
  const cx = (box.min.x + box.max.x) / 2;
  const cz = (box.min.z + box.max.z) / 2;
  const dx = other.x - cx;
  const dz = other.z - cz;
  if (Math.abs(dx) > Math.abs(dz)) {
    return dx > 0 ? "+x" : "-x";
  }
  return dz > 0 ? "+z" : "-z";
}

/** Entry point along the chosen face. `slot` slides the entry along the
 * face's tangent so multiple cables emerging from the same side fan out
 * in a parallel grid instead of stacking on top of each other.
 *
 *   slot = 0 → centered.
 *   slot = ±1, ±2, ... → offset by `spacing` per slot from centre.
 */
function sideEntryPoint(box: THREE.Box3, side: Side, slot = 0, spacing = 0): THREE.Vector3 {
  const cx = (box.min.x + box.max.x) / 2;
  const cz = (box.min.z + box.max.z) / 2;
  // Use the bbox's actual base Y so links exit at the same plane the
  // object sits on. Scene view: every object sits on Y=0 → this is 0,
  // identical to before. Iso view: each plate lives at its own world Y
  // and the bbox carries that — without this the cables would flatten
  // back to Y=0 (i.e. the bottom of the world) regardless of which
  // plate the source object is on.
  const baseY = box.min.y;
  // Clamp the slot offset so it never escapes the actual face length.
  const halfX = (box.max.x - box.min.x) / 2;
  const halfZ = (box.max.z - box.min.z) / 2;
  const off = slot * spacing;
  switch (side) {
    // ±X faces are tangent along Z.
    case "+x": return new THREE.Vector3(box.max.x, baseY, cz + Math.max(-halfZ * 0.85, Math.min(halfZ * 0.85, off)));
    case "-x": return new THREE.Vector3(box.min.x, baseY, cz + Math.max(-halfZ * 0.85, Math.min(halfZ * 0.85, off)));
    // ±Z faces are tangent along X.
    case "+z": return new THREE.Vector3(cx + Math.max(-halfX * 0.85, Math.min(halfX * 0.85, off)), baseY, box.max.z);
    case "-z": return new THREE.Vector3(cx + Math.max(-halfX * 0.85, Math.min(halfX * 0.85, off)), baseY, box.min.z);
  }
}

/** Outward normal of a side, in XZ. */
function sideNormal(side: Side): THREE.Vector3 {
  switch (side) {
    case "+x": return new THREE.Vector3(1, 0, 0);
    case "-x": return new THREE.Vector3(-1, 0, 0);
    case "+z": return new THREE.Vector3(0, 0, 1);
    case "-z": return new THREE.Vector3(0, 0, -1);
  }
}

/**
 * Build a strict-orthogonal Manhattan path: every internal segment is
 * pure-X or pure-Z, no diagonals. Each end emerges perpendicular to its
 * picked face for a stub, then turns once and runs straight to the other
 * end's stub.
 *
 * Two cases:
 *   • Same-axis: both stubs run along the same world axis (e.g. both
 *     left/right faces). One bend in the perpendicular axis at the
 *     midpoint between the two stub ends.
 *   • Cross-axis: stubs run along different axes. One corner where the
 *     two stub lines intersect — that corner makes both segments
 *     axis-aligned by construction.
 *
 * No channel snapping, no obstacle detour — kept deliberately simple
 * after a more elaborate solver introduced visual noise. If two cables
 * happen to share a midpoint they overlap naturally; otherwise each one
 * takes the shortest clean Manhattan path.
 */
const STRAIGHT_STUB = 0.0;
const ELBOW_STUB = 0.6;

function buildPerpLinkPath(
  a: THREE.Vector3,
  aSide: Side,
  b: THREE.Vector3,
  bSide: Side,
  straight: boolean,
): THREE.Vector3[] {
  const stubLen = straight ? STRAIGHT_STUB : ELBOW_STUB;
  const aNorm = sideNormal(aSide);
  const bNorm = sideNormal(bSide);
  const aOut = a.clone().addScaledVector(aNorm, stubLen);
  const bOut = b.clone().addScaledVector(bNorm, stubLen);

  // Cable Y. When endpoints share a plate (the iso case) we inherit;
  // otherwise we average. Preserved through every waypoint — strict
  // orthogonality applies in XZ only.
  const linkY = (a.y + b.y) / 2;

  // Are the two stubs along the same world axis?
  const aAxisX = Math.abs(aNorm.x) > 0.5;
  const bAxisX = Math.abs(bNorm.x) > 0.5;
  const sameAxis = aAxisX === bAxisX;

  if (sameAxis) {
    // Bend perpendicular at the midpoint between the two stub ends.
    if (aAxisX) {
      const midX = (aOut.x + bOut.x) / 2;
      return [
        a, aOut,
        new THREE.Vector3(midX, linkY, aOut.z),
        new THREE.Vector3(midX, linkY, bOut.z),
        bOut, b,
      ];
    }
    const midZ = (aOut.z + bOut.z) / 2;
    return [
      a, aOut,
      new THREE.Vector3(aOut.x, linkY, midZ),
      new THREE.Vector3(bOut.x, linkY, midZ),
      bOut, b,
    ];
  }

  // Cross-axis: one corner where the stub lines intersect. The path
  //   aOut → corner → bOut
  // is automatically pure-X then pure-Z (or vice versa).
  const cornerX = aAxisX ? bOut.x : aOut.x;
  const cornerZ = aAxisX ? aOut.z : bOut.z;
  return [
    a, aOut,
    new THREE.Vector3(cornerX, linkY, cornerZ),
    bOut, b,
  ];
}

// Build a polyline tube along right-angle waypoints with a flow shader.
function buildPolylineTube(
  points: THREE.Vector3[],
  color: THREE.Color,
  thickness: number,
  cableOpacity: number,
  stripOpacity: number,
  power: boolean,
): THREE.Mesh {
  const pts = points.filter((p, i) => i === 0 || !p.equals(points[i - 1]));
  if (pts.length < 2) {
    const m = new THREE.Mesh();
    return m;
  }
  const path = new THREE.CurvePath<THREE.Vector3>();
  for (let i = 0; i < pts.length - 1; i++) {
    path.add(new THREE.LineCurve3(pts[i], pts[i + 1]));
  }
  const totalSegments = Math.max(2, (pts.length - 1) * 4);
  const geom = new THREE.TubeGeometry(path as any, totalSegments, thickness, 6, false);
  const mat = buildLinkShader(color, cableOpacity, stripOpacity, power);
  const m = new THREE.Mesh(geom, mat);
  m.name = "link";
  return m;
}

function buildLinkShader(
  color: THREE.Color,
  cableOpacity: number,
  stripOpacity: number,
  power: boolean,
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: color },
      uTime: { value: 0 },
      uSpeed: { value: 0.2 },
      // Two opacities: cable (steady tube) and strip (the flowing pulse).
      uCableA: { value: cableOpacity },
      uStripA: { value: stripOpacity },
      // Power on = flow animates and the strip multiplier is full-strength.
      // Power off = flow scrolls but is muted to a faint static stripe.
      uPower: { value: power ? 1.0 : 0.0 },
    },
    transparent: true,
    depthWrite: false,
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying vec2 vUv;
      uniform vec3 uColor;
      uniform float uTime;
      uniform float uSpeed;
      uniform float uCableA;
      uniform float uStripA;
      uniform float uPower;

      void main() {
        vec3 base = uColor * 0.85;
        // Flowing pulse: stripes scrolling along uv.x. When power is off,
        // the time-scroll stops so the pulse freezes flat.
        float t = uTime * uSpeed * uPower;
        float stripe = fract(vUv.x * 4.0 - t);
        float pulse = smoothstep(0.85, 0.99, stripe) * uPower;
        vec3 col = base + pulse * uColor * 1.5;
        float alpha = uCableA + pulse * uStripA;
        gl_FragColor = vec4(col, alpha);
      }
    `,
  });
}

export type LinkBuildOpts = {
  objectsById: Map<string, SceneObject>;
  worldGroup: THREE.Group;
  thickness: number;
  color: THREE.Color;
  straight: boolean;
  cableOpacity: number;
  stripOpacity: number;
  power: boolean;
};

// (Obstacle-aware routing + Manhattan-snap helpers were removed — see
// git log. The simple orthogonal L-builder above produced cleaner
// paths without the extra solver.)

/**
 * Rebuild every link mesh from the connection list. Disposes prior link
 * meshes and clears the group.
 */
export function rebuildLinks(
  linksGroup: THREE.Group,
  links: LinkConnection[],
  opts: LinkBuildOpts,
): void {
  // Dispose old.
  for (const child of [...linksGroup.children]) {
    linksGroup.remove(child);
    const m = child as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    if (m.material) {
      const arr = Array.isArray(m.material) ? m.material : [m.material];
      for (const mat of arr) (mat as THREE.Material).dispose();
    }
  }
  if (links.length === 0) return;

  // Build temp world boxes for every object referenced by any link.
  const boxes = new Map<string, THREE.Box3>();
  for (const link of links) {
    for (const id of [link.fromId, link.toId]) {
      if (boxes.has(id)) continue;
      const o = opts.worldGroup.children.find((c) => c.userData?.id === id);
      if (!o) continue;
      const box = new THREE.Box3().setFromObject(o);
      boxes.set(id, box);
    }
  }

  // Pre-pass: pick each end's face, then group cables by (objectId, side) so
  // multiple links exiting the same face can be slotted into a parallel
  // grid instead of stacking on top of each other. Slot 0 is centred; the
  // rest fan out in ±1, ±2 increments, ordered by the angle each link
  // makes with the face tangent so neighbouring slots share neighbouring
  // physical paths (no crossings at the source).
  type EndSpec = { box: THREE.Box3; side: Side; otherCenter: THREE.Vector3 };
  type LinkPlan = { link: LinkConnection; a: EndSpec; b: EndSpec };
  const plans: LinkPlan[] = [];
  for (const link of links) {
    const ba = boxes.get(link.fromId);
    const bb = boxes.get(link.toId);
    if (!ba || !bb) continue;
    const aCenter = new THREE.Vector3((ba.min.x + ba.max.x) / 2, 0, (ba.min.z + ba.max.z) / 2);
    const bCenter = new THREE.Vector3((bb.min.x + bb.max.x) / 2, 0, (bb.min.z + bb.max.z) / 2);
    const aSide = pickSide(ba, bCenter);
    const bSide = pickSide(bb, aCenter);
    plans.push({
      link,
      a: { box: ba, side: aSide, otherCenter: bCenter },
      b: { box: bb, side: bSide, otherCenter: aCenter },
    });
  }

  // Group links by (objectId|side). Each group's members get integer slots
  // [-(n-1)/2 .. +(n-1)/2]. Sort within group by tangent-projection of the
  // far end so slots ascend along the face — the neighbour relationship in
  // physical space matches the slot order.
  type GroupKey = string;
  const groupKey = (id: string, side: Side): GroupKey => `${id}|${side}`;
  const groupMembers = new Map<GroupKey, Array<{ planIdx: number; end: "a" | "b"; tan: number }>>();
  function tangentScalar(side: Side, p: THREE.Vector3): number {
    // ±X face: tangent runs along Z. ±Z face: tangent along X. We sort by
    // the corresponding component so slots fan along the face cleanly.
    return (side === "+x" || side === "-x") ? p.z : p.x;
  }
  plans.forEach((p, i) => {
    const aKey = groupKey(p.link.fromId, p.a.side);
    const bKey = groupKey(p.link.toId,   p.b.side);
    if (!groupMembers.has(aKey)) groupMembers.set(aKey, []);
    if (!groupMembers.has(bKey)) groupMembers.set(bKey, []);
    groupMembers.get(aKey)!.push({ planIdx: i, end: "a", tan: tangentScalar(p.a.side, p.a.otherCenter) });
    groupMembers.get(bKey)!.push({ planIdx: i, end: "b", tan: tangentScalar(p.b.side, p.b.otherCenter) });
  });

  // Compute the slot index for each (planIdx, end). Spacing scales with
  // cable thickness so multi-cable bundles look like neat parallel runs
  // even when the user changes the link thickness slider.
  const slotSpacing = Math.max(0.06, opts.thickness * 3);
  const slotMap = new Map<string, number>(); // `${planIdx}|${end}` → slot offset (in metres)
  for (const members of groupMembers.values()) {
    members.sort((m1, m2) => m1.tan - m2.tan);
    const n = members.length;
    members.forEach((m, idx) => {
      const slot = idx - (n - 1) / 2; // centred around 0
      slotMap.set(`${m.planIdx}|${m.end}`, slot * slotSpacing);
    });
  }

  // Build the tubes. No obstacle detour — the previous solver + the
  // channel-snap pass produced visually noisy paths in dense scenes.
  // buildPerpLinkPath alone gives a clean Manhattan L (or Z-shape for
  // same-axis stubs) that connects two objects with at most two corners.
  plans.forEach((plan, i) => {
    const aOff = slotMap.get(`${i}|a`) ?? 0;
    const bOff = slotMap.get(`${i}|b`) ?? 0;
    const aEntry = sideEntryPoint(plan.a.box, plan.a.side, aOff, 1);
    const bEntry = sideEntryPoint(plan.b.box, plan.b.side, bOff, 1);
    aEntry.y = 0.05;
    bEntry.y = 0.05;
    const points = buildPerpLinkPath(aEntry, plan.a.side, bEntry, plan.b.side, opts.straight);

    const mesh = buildPolylineTube(
      points,
      opts.color,
      opts.thickness,
      opts.cableOpacity,
      opts.stripOpacity,
      opts.power,
    );
    mesh.userData.linkId = plan.link.id;
    linksGroup.add(mesh);
  });
}

/**
 * Tick all link shaders so the flow animation progresses.
 */
export function tickLinks(linksGroup: THREE.Group, t: number, speed: number): void {
  linksGroup.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.material) return;
    const arr = Array.isArray(m.material) ? m.material : [m.material];
    for (const mat of arr) {
      const sm = mat as THREE.ShaderMaterial;
      if (sm.uniforms?.uTime) sm.uniforms.uTime.value = t;
      if (sm.uniforms?.uSpeed) sm.uniforms.uSpeed.value = speed;
    }
  });
}
